import { useState } from "react";
import { PageHeader } from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";
import { useSite } from "@/context/SiteContext";
import { gradeLabel } from "@/data/content";
import { Search } from "lucide-react";

const POSITIONS = ["All", "P", "C", "IF", "OF"];

const matchesPos = (p, filter) => {
  if (filter === "All") return true;
  if (filter === "IF") return /^(SS|2B|3B|1B|IF)/.test(p.pos) || p.pos.includes("B");
  if (filter === "P") return p.pos.includes("P");
  if (filter === "C") return p.pos.includes("C");
  if (filter === "OF") return p.pos.includes("OF");
  return true;
};

export default function Roster() {
  const { site } = useSite();
  const [query, setQuery] = useState("");
  const [pos, setPos] = useState("All");

  const players = [...site.players]
    .sort((a, b) => (a.num ?? 99) - (b.num ?? 99))
    .filter((p) => matchesPos(p, pos) && p.name.toLowerCase().includes(query.toLowerCase()));

  return (
    <div data-testid="roster-page">
      <PageHeader
        eyebrow="The Bears"
        title="Team Roster"
        subtitle={site.content.subtitle_roster}
        testid="roster-header"
      />

      <section className="py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between mb-8">
              <div className="relative w-full sm:max-w-xs">
                <Search size={17} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search players..."
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  data-testid="roster-search-input"
                  className="w-full pl-10 pr-4 py-2.5 rounded-md border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#0F4C2A]/40 focus:border-[#0F4C2A]"
                />
              </div>
              <div className="flex gap-2 flex-wrap" data-testid="roster-position-filter">
                {POSITIONS.map((p) => (
                  <button
                    key={p}
                    onClick={() => setPos(p)}
                    data-testid={`roster-filter-${p.toLowerCase()}`}
                    className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wide transition-colors ${
                      pos === p ? "bg-[#0F4C2A] text-white shadow-md" : "bg-white border border-slate-200 text-slate-600 hover:border-[#0F4C2A]/40"
                    }`}
                  >
                    {p === "All" ? "All" : p === "IF" ? "Infield" : p === "OF" ? "Outfield" : p === "P" ? "Pitchers" : "Catchers"}
                  </button>
                ))}
              </div>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="roster-container">
            {players.map((p, i) => (
              <Reveal key={p.id || p.num} delay={(i % 4) * 0.06}>
                <div className="card-lift relative bg-white rounded-lg border border-slate-200 p-6 overflow-hidden hover:border-[#0F4C2A]/40" data-testid={`player-card-${p.num}`}>
                  {p.photo ? (
                    <img src={p.photo} alt={p.name} className="h-20 w-20 rounded-full object-cover ring-2 ring-[#0F4C2A]/15 mb-4" />
                  ) : (
                    <span className="font-stat absolute -top-3 -right-2 text-7xl font-bold text-[#0F4C2A]/8 select-none">{p.num}</span>
                  )}
                  <div className="flex items-center gap-2 mb-3">
                    <span className="bg-[#0F4C2A] text-white font-stat font-bold text-sm px-2.5 py-1 rounded-sm">#{p.num}</span>
                    <span className="bg-[#D97706]/15 text-[#D97706] text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-sm">{p.pos}</span>
                  </div>
                  <h3 className="font-condensed text-2xl font-bold uppercase tracking-tight text-slate-900">{p.name}</h3>
                  <div className="mt-3 flex items-center gap-4 text-xs text-slate-500 font-semibold uppercase tracking-wide">
                    <span>{gradeLabel(p.grade)}</span>
                    <span className="text-slate-300">|</span>
                    <span>B/T: {p.bt}</span>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          {players.length === 0 && (
            <p className="text-center text-slate-500 py-16" data-testid="roster-empty">No players match your search.</p>
          )}
        </div>
      </section>
    </div>
  );
}
